import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, KeyRound, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";
import { formatPhone } from "../lib/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "border-[#F0DDE0] focus-visible:ring-[#C15B6B]";
const btnCls = "w-full h-11 rounded-xl bg-[#9E2A3B] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#861f2f] transition-colors disabled:opacity-60";

export const ForgotPasswordDialog = ({ open, onOpenChange, purpose, initialPhone = "" }) => {
  const { toast } = useToast();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPhone(initialPhone); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = (o) => {
    if (!o) { setStep("phone"); setCode(""); setPass(""); }
    onOpenChange(o);
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "İşlem başarısız." }); }
    finally { setBusy(false); }
  };

  const send = () => run(async () => {
    const { data } = await axios.post(`${API}/password-reset/send`, { phone, purpose });
    if (data.smsOff) { setStep("contact"); return; }
    setStep("code");
    toast({ title: "Kod gönderildi", description: "Telefonunuza gelen 6 haneli kodu girin." });
  });

  const confirm = () => run(async () => {
    await axios.post(`${API}/password-reset/confirm`, { phone, purpose, code, password: pass });
    toast({ title: "Şifreniz değiştirildi", description: "Yeni şifrenizle giriş yapabilirsiniz." });
    close(false);
  });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-sm" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-[#2A2224]">
            <KeyRound className="w-5 h-5 text-[#9E2A3B]" /> Şifremi Unuttum
          </DialogTitle>
          <DialogDescription>
            {step === "phone" && "Kayıtlı telefon numaranızı girin, size SMS ile kod gönderelim."}
            {step === "code" && `${phone} numarasına gelen kodu ve yeni şifrenizi girin.`}
            {step === "contact" && "SMS ile sıfırlama şu an kullanılamıyor."}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" && (
          <div className="space-y-3">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => setPhone(formatPhone(phone))} inputMode="tel" placeholder="05XX XXX XX XX" data-testid="forgot-phone-input" className={inputCls} />
            <button onClick={send} disabled={busy || phone.replace(/\D/g, "").length < 10} data-testid="forgot-send-btn" className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Kod Gönder
            </button>
          </div>
        )}

        {step === "code" && (
          <div className="space-y-3">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="6 haneli kod" data-testid="forgot-code-input" className={`${inputCls} text-center tracking-[0.4em]`} />
            <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Yeni şifre (en az 4 karakter)" data-testid="forgot-newpass-input" className={inputCls} />
            <button onClick={confirm} disabled={busy || code.length !== 6 || pass.length < 4} data-testid="forgot-confirm-btn" className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Şifremi Değiştir
            </button>
            <button onClick={send} disabled={busy} data-testid="forgot-resend-btn" className="w-full text-sm text-[#9E2A3B] hover:underline">Kod gelmedi mi? Tekrar gönder</button>
          </div>
        )}

        {step === "contact" && (
          <div className="rounded-xl bg-[#FBF5F0] p-4 text-sm text-[#5b4d4f]" data-testid="forgot-contact-msg">
            Şifrenizi sıfırlamak için lütfen bizimle iletişime geçin. Telefon numaranızı yazmanız yeterli, size yardımcı olalım.
            <a href="mailto:info@dugunsenin.com" className="mt-3 flex items-center gap-2 font-medium text-[#9E2A3B] hover:underline">
              <Mail className="w-4 h-4" /> info@dugunsenin.com
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
